// NAICS Code auto-selection from the GL Pricing classifications.
// Mirrors the FSE "Rating and Pricing" workbook ('Primary Rating' E18/E19):
// the NAICS code follows the ISO class code carrying the largest total rated
// GL premium, ignoring the liquor classes — liquor is rated as a coverage on
// top of the risk, not as the insured's industry.
//
// The ISO -> NAICS crosswalk lives on the `gl_exposures` static-data config,
// as a `naics` entry on each class code, so a company without the crosswalk
// loaded simply never derives a value.

// Liquor-eligible class codes, mirroring `united_es_gl_v1/config.py`
// LIQUOR_CLASS_CODES. Priced rows carry `is_liquor_class`; the list is the
// fallback for results stored before that field existed.
export const LIQUOR_CLASS_CODES = new Set([
  "50911",
  "58161",
  "58165",
  "58166",
  "58168",
  "59211",
  "70412",
]);

const isLiquorRow = (row) =>
  row.is_liquor_class ?? LIQUOR_CLASS_CODES.has(`${row.class_code}`);

// The priced classification row with the largest total rated GL premium,
// ignoring liquor classes. Ties keep the row that comes first in the table,
// matching the workbook's top-down MAX lookup. Rows with no premium are not
// candidates — an unrated table must not pick an arbitrary class.
export const dominantClassification = (records = []) =>
  records.reduce((best, row) => {
    if (isLiquorRow(row) || !(row.total_premium > 0)) return best;
    return best == null || row.total_premium > best.total_premium ? row : best;
  }, null);

// { code, title } for a class code, from the crosswalk on the `gl_exposures`
// config. A class code that is missing from the crosswalk derives nothing.
export const naicsForClassCode = (classCodes = [], classCode) => {
  const entry = classCodes.find((c) => `${c.code}` === `${classCode}`);
  const naics = entry?.naics;
  if (naics?.code == null) return null;
  return { code: naics.code, title: naics.title ?? "" };
};

// The NAICS to fill in for a set of priced classifications, or null when it
// cannot be derived (no priced rows, only liquor classes, class code absent
// from the crosswalk, crosswalk not loaded).
export const deriveNaics = (records, classCodes) => {
  const dominant = dominantClassification(records);
  if (dominant == null) return null;
  return naicsForClassCode(classCodes, dominant.class_code);
};
